import { useWebsiteSettings } from 'client/store'
import { PageLocaleEnum } from 'common/enums/PageLocaleEnum'
import { PageTypeEnum } from 'common/enums/PageTypeEnum'
import React, { useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutationWithValidation } from 'src/editor/api/hooks/use-api-mutation-with-validation'
import { websitePagesQuery } from 'src/editor/api/queries/website-pages-query'
import { addWebsitePageLocale } from 'src/editor/api/website-pages-api'
import { LanguageSelector } from 'src/editor/shared/ui-lib/language-selector'
import { getLocalesOptions } from 'src/editor/shared/ui-lib/language-selector/utils/get-locales-options'
import {
  getWebsiteId,
  getWebsiteLocale,
  getWebsiteLocales,
} from 'src/editor/store/website-settings/website-settings-selector'
import { AddPageLocale } from './add-page-locale'
import { RemovePageLocale } from './remove-page-locale'

interface LocalesSettingsTabProps {
  pageType: PageTypeEnum
  websitePageSetId: number
}

export const LocalesSettingsTab = ({
  pageType,
  websitePageSetId,
}: LocalesSettingsTabProps) => {
  const { t } = useTranslation()

  const websiteId = useWebsiteSettings(getWebsiteId) as number
  const websiteLocale = useWebsiteSettings(getWebsiteLocale)
  const websiteLocales = useWebsiteSettings(getWebsiteLocales)

  const [localeToRemove, setLocaleToRemove] = useState<PageLocaleEnum | null>(
    null,
  )
  const [copyPagesLocale, setCopyPagesLocale] = useState<PageLocaleEnum | null>(
    null,
  )
  const pendingLocaleToAddRef = useRef<PageLocaleEnum | null>(null)

  const { data, isFetching } = websitePagesQuery.useQuery({
    queryFnArgs: [websiteId],
  })
  const { fetcher: fetcherAddLocale, isLoading: isLoadingAddLocale } =
    useMutationWithValidation({
      requestFunction: addWebsitePageLocale,
      successToastMessage: t(
        'page_settings.locales.add_locale.added_successfully',
      ),
      handleSuccess: (_, [{ locale }]) => {
        setCopyPagesLocale(locale)
      },
      handleFinally: () => {
        pendingLocaleToAddRef.current = null
      },
      invalidateKeyOnSuccess: websitePagesQuery.getQueryKey(websiteId),
    })

  const pageLocales = useMemo(
    () =>
      data?.pages
        .find(page => page.id === websitePageSetId)
        ?.locales.map(locale => locale.locale) ?? [],
    [data?.pages, websitePageSetId],
  )

  const localeOptions = useMemo(() => {
    return getLocalesOptions({
      t,
      localeList: websiteLocales,
      setAdditionalProperties: locale => ({
        nonRemovable: websiteLocale === locale,
      }),
    })
  }, [t, websiteLocales, websiteLocale])

  const handleChangeLocales = (locales: PageLocaleEnum[]) => {
    if (pageLocales.length > locales.length) {
      const localeToRemove = pageLocales.find(
        locale => !locales.includes(locale),
      )
      if (localeToRemove) {
        setLocaleToRemove(localeToRemove)
      }
    } else if (pageLocales.length < locales.length) {
      const localeToAdd = locales.find(locale => !pageLocales.includes(locale))
      if (localeToAdd && pendingLocaleToAddRef.current !== localeToAdd) {
        pendingLocaleToAddRef.current = localeToAdd
        fetcherAddLocale({
          websitePageSetId,
          locale: localeToAdd,
        })
      }
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <span className="text-heading-4">{t('page_settings.locales.title')}</span>
      <span className="text-body-M text-textTertiary">
        {t('page_settings.locales.description')}
      </span>
      <LanguageSelector
        isMulti
        menuPortalTarget={document.body}
        isLoading={isFetching || isLoadingAddLocale}
        isDisabled={
          pageType === PageTypeEnum.WebsiteHome ||
          isLoadingAddLocale ||
          !!localeToRemove
        }
        selectedLocales={pageLocales}
        options={localeOptions}
        onChange={handleChangeLocales}
      />
      <AddPageLocale
        websitePageSetId={websitePageSetId}
        copyPagesLocale={copyPagesLocale}
        onClose={() => setCopyPagesLocale(null)}
      />
      <RemovePageLocale
        websitePageSetId={websitePageSetId}
        localeToRemove={localeToRemove}
        onClose={() => setLocaleToRemove(null)}
      />
    </div>
  )
}
